/**
 * Voice Browser Modal
 *
 * Modal for browsing voices available from TTS provider API.
 * Shows voice list with Test and Add to Database actions.
 *
 * Acceptance Criteria: AC 26, 34
 */

import { useState, useMemo } from 'react';
import {
  useProviderVoices,
  useVoices,
  type TTSProvider,
  type ProviderVoiceItem,
} from '../services/voiceApi';
import { TestVoiceModal } from './TestVoiceModal';
import { AddVoiceForm } from './AddVoiceForm';

interface VoiceBrowserModalProps {
  isOpen: boolean;
  onClose: () => void;
  provider: TTSProvider;
}

export function VoiceBrowserModal({ isOpen, onClose, provider }: VoiceBrowserModalProps) {
  const { data: voices, isLoading, error } = useProviderVoices(isOpen ? provider.id : undefined);
  const { data: existingVoices = [] } = useVoices({ providerId: provider.id });
  const [selectedVoice, setSelectedVoice] = useState<ProviderVoiceItem | null>(null);
  const [showTestModal, setShowTestModal] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [expandedVoices, setExpandedVoices] = useState<Set<string>>(new Set());
  const [languageFilter, setLanguageFilter] = useState<string>('all');
  const [hideAlreadyAdded, setHideAlreadyAdded] = useState(true);

  const handleTestVoice = (voice: ProviderVoiceItem) => {
    setSelectedVoice(voice);
    setShowTestModal(true);
  };

  const handleAddVoice = (voice: ProviderVoiceItem) => {
    setSelectedVoice(voice);
    setShowAddForm(true);
  };

  const toggleMetadata = (voiceId: string) => {
    setExpandedVoices((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(voiceId)) {
        newSet.delete(voiceId);
      } else {
        newSet.add(voiceId);
      }
      return newSet;
    });
  };

  // Extract unique languages from voices and sort alphabetically
  const availableLanguages = useMemo(() => {
    if (!voices) return [];
    const languageSet = new Set<string>();
    voices.forEach((voice) => {
      voice.languages.forEach((lang) => languageSet.add(lang));
    });
    return Array.from(languageSet).sort();
  }, [voices]);

  // Filter voices by selected language and "hide already added" toggle
  const filteredVoices = useMemo(() => {
    if (!voices) return [];
    let filtered = voices;

    // Apply language filter
    if (languageFilter !== 'all') {
      filtered = filtered.filter((voice) => voice.languages.includes(languageFilter));
    }

    // Apply "hide already added" filter
    if (hideAlreadyAdded) {
      filtered = filtered.filter((voice) => {
        return !existingVoices.some((v) => v.providerVoiceId === voice.providerVoiceId);
      });
    }

    return filtered;
  }, [voices, languageFilter, hideAlreadyAdded, existingVoices]);

  // Check if a voice is already in the database
  const isVoiceInDatabase = (providerVoiceId: string) => {
    return existingVoices.some((v) => v.providerVoiceId === providerVoiceId);
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        data-testid="voice-browser-modal"
      >
        <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-6xl max-h-[85vh] flex flex-col">
          {/* Header */}
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold">Browse {provider.name} Voices</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
              data-testid="close-button"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Filters */}
          {!isLoading && !error && (
            <div className="mb-4 space-y-3">
              {/* Language Filter */}
              {availableLanguages.length > 0 && (
                <div>
                  <label
                    htmlFor="language-filter"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Filter by Language
                  </label>
                  <select
                    id="language-filter"
                    value={languageFilter}
                    onChange={(e) => setLanguageFilter(e.target.value)}
                    className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="language-filter"
                  >
                    <option value="all">All Languages ({voices?.length || 0})</option>
                    {availableLanguages.map((lang) => (
                      <option key={lang} value={lang}>
                        {lang.toUpperCase()} (
                        {voices?.filter((v) => v.languages.includes(lang)).length || 0})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Hide Already Added Filter */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="hide-already-added"
                  checked={hideAlreadyAdded}
                  onChange={(e) => setHideAlreadyAdded(e.target.checked)}
                  className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  data-testid="hide-already-added-toggle"
                />
                <label htmlFor="hide-already-added" className="text-sm text-gray-700">
                  Hide voices already added to database ({existingVoices.length} total)
                </label>
              </div>
            </div>
          )}

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {isLoading && (
              <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
              </div>
            )}

            {error && (
              <div className="text-center py-12" data-testid="error-message">
                <p className="text-red-600 text-lg mb-2">Error loading voices</p>
                <p className="text-gray-600 mb-4">{error.message}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  data-testid="retry-button"
                >
                  Retry
                </button>
                {error.message.includes('API key') && (
                  <button
                    onClick={onClose}
                    className="ml-2 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                    data-testid="configure-button"
                  >
                    Configure API Key
                  </button>
                )}
              </div>
            )}

            {!isLoading && !error && voices && (
              <div className="space-y-3">
                <p className="text-sm text-gray-600 mb-4">
                  {languageFilter === 'all'
                    ? `Found ${voices.length} voices. These voices are not saved to the database yet.`
                    : `Showing ${filteredVoices.length} of ${voices.length} voices for ${languageFilter.toUpperCase()}.`}
                </p>

                {filteredVoices.map((voice) => (
                  <div
                    key={voice.providerVoiceId}
                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition"
                    data-testid="voice-item"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="font-semibold text-lg" data-testid="voice-name">
                            {voice.name}
                          </h3>
                          <span
                            className="text-xs px-2 py-1 bg-gray-200 text-gray-700 rounded"
                            data-testid="voice-id"
                          >
                            {voice.providerVoiceId}
                          </span>
                        </div>

                        {/* Language & Locale Badges */}
                        {(voice.languages.length > 0 || voice.locale) && (
                          <div className="flex flex-wrap gap-2 mb-2">
                            {voice.languages.map((lang) => (
                              <span
                                key={lang}
                                className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium"
                                data-testid={`language-badge-${lang}`}
                              >
                                {lang.toUpperCase()}
                              </span>
                            ))}
                            {voice.locale && (
                              <span
                                className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium"
                                data-testid="locale-badge"
                              >
                                {voice.locale}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Metadata Toggle */}
                        {Object.keys(voice.metadata).length > 0 && (
                          <button
                            onClick={() => toggleMetadata(voice.providerVoiceId)}
                            className="text-sm text-blue-600 hover:text-blue-800 mb-2"
                            data-testid="toggle-metadata-button"
                          >
                            {expandedVoices.has(voice.providerVoiceId)
                              ? '▼ Hide Metadata'
                              : '▶ Show Metadata'}
                          </button>
                        )}

                        {/* Expandable Metadata */}
                        {expandedVoices.has(voice.providerVoiceId) && (
                          <pre
                            className="text-xs bg-gray-100 p-3 rounded overflow-x-auto"
                            data-testid="voice-metadata"
                          >
                            {JSON.stringify(voice.metadata, null, 2)}
                          </pre>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex space-x-2 ml-4">
                        <button
                          onClick={() => handleTestVoice(voice)}
                          className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-sm rounded transition-colors"
                          data-testid="test-voice-button"
                        >
                          Test
                        </button>
                        <button
                          onClick={() => handleAddVoice(voice)}
                          disabled={isVoiceInDatabase(voice.providerVoiceId)}
                          className={`px-3 py-1 text-white text-sm rounded transition-colors ${
                            isVoiceInDatabase(voice.providerVoiceId)
                              ? 'bg-gray-400 cursor-not-allowed'
                              : 'bg-blue-600 hover:bg-blue-700'
                          }`}
                          data-testid="add-to-database-button"
                          title={
                            isVoiceInDatabase(voice.providerVoiceId)
                              ? 'Voice already in database'
                              : 'Add to database'
                          }
                        >
                          {isVoiceInDatabase(voice.providerVoiceId)
                            ? 'Already Added'
                            : 'Add to Database'}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sub-modals */}
      {selectedVoice && (
        <>
          <TestVoiceModal
            isOpen={showTestModal}
            onClose={() => {
              setShowTestModal(false);
              setSelectedVoice(null);
            }}
            provider={provider}
            providerVoiceId={selectedVoice.providerVoiceId}
            voiceName={selectedVoice.name}
            languages={selectedVoice.languages}
          />

          <AddVoiceForm
            isOpen={showAddForm}
            onClose={() => {
              setShowAddForm(false);
              setSelectedVoice(null);
            }}
            provider={provider}
            providerVoiceId={selectedVoice.providerVoiceId}
            voiceName={selectedVoice.name}
            metadata={{
              ...selectedVoice.metadata,
              ...(selectedVoice.locale ? { locale: selectedVoice.locale } : {}),
            }}
            languages={selectedVoice.languages}
          />
        </>
      )}
    </>
  );
}
